import api from '../axios.config';
import { SpringPage } from '../../types/billing';

/**
 * GST tax invoices and credit notes (backend docs/design/2026-10-14_gst_tax_invoices.md).
 * The backend answers 404 to every call while `cpass.billing.tax-invoices.enabled` is off,
 * so `disabled: true` means "hide the section", not an error.
 */
export const TAX_INVOICE_ENDPOINTS = {
  LIST: '/billing/tax-invoices',
  PDF: (id: number) => `/billing/tax-invoices/${id}/pdf`,
};

export interface TaxDocumentView {
  id: number;
  documentType: 'TAX_INVOICE' | 'CREDIT_NOTE';
  status: 'DRAFT' | 'ISSUED' | 'CANCELLED';
  documentNumber: string | null;
  financialYear: string | null;
  issueDate: string | null;
  issuerOrganizationId: string;
  issuerName: string | null;
  issuerGstin: string;
  recipientOrganizationId: string;
  recipientName: string | null;
  recipientGstin: string | null;
  sourceInvoiceNumber: string | null;
  supplyType: 'INTRA_STATE' | 'INTER_STATE' | 'EXPORT_LUT' | 'SEZ_LUT';
  placeOfSupplyCode: string;
  currency: string;
  taxableValue: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
}

export type ListResult =
  | { success: true; data: SpringPage<TaxDocumentView> }
  | { success: false; disabled: boolean; error: string };

export const taxInvoicesService = {
  async list(params: { organizationId?: string; page?: number; size?: number }): Promise<ListResult> {
    try {
      const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
      const r = await api.get(TAX_INVOICE_ENDPOINTS.LIST, { params: clean });
      return { success: true, data: r.data };
    } catch (error: any) {
      const status = error?.response?.status;
      return { success: false, disabled: status === 404, error: status === 404 ? 'Not available' : 'Failed to load tax invoices' };
    }
  },

  /** Fetched as a blob (the endpoint needs the Authorization header), saved under the server's name. */
  async downloadPdf(doc: Pick<TaxDocumentView, 'id' | 'documentNumber'>): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await api.get(TAX_INVOICE_ENDPOINTS.PDF(doc.id), { responseType: 'blob' });
      const disposition = String(res.headers?.['content-disposition'] || '');
      const named = /filename="?([^"]+)"?/.exec(disposition)?.[1];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = named || `${(doc.documentNumber || `draft-${doc.id}`).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
      return { ok: true };
    } catch (error: any) {
      return { ok: false, error: error?.response?.status === 404 ? 'Not found' : 'Download failed' };
    }
  },
};
